import React, { cloneElement, useEffect, useState } from 'react';
import styled from '@mui/system/styled';
import PropTypes from 'prop-types';

import { SubmitButton as DefaultSubmitButton } from '../Inputs';

import FormControl from '@mui/material/FormControl';
import { Stack, Typography } from '@mui/material';

const FormController = (props) => {
	let { children, options, useHook } = props;
	const { error, gap = 2, disabled, onSubmit, SubmitButton } = props;
	const optionDefaults = {
		disableSubmitButton: false,
		disableValidation: false,
		flexDirection: 'column',
		submitButtonText: undefined
	};

	options = Object.assign({}, optionDefaults, options);

	const [hasErrors, setHasErrors] = useState({});
	const [renderedChildren, setRenderedChildren] = useState([]);
	const [formValues, setFormValues] = useState(null);
	const [requiredFormValues, setRequiredFormValues] = useState([]);
	const [submitIsDisabled, setSubmitIsDisabled] = useState(true);

	useEffect(() => {
		renderChildren();
		if (useHook) {
			useHook(formValues);
		}
	}, [formValues, children]);

	useEffect(() => {
		if (renderedChildren.length && validateFormSubmit()) {
			setSubmitIsDisabled(false);
		} else if (!submitIsDisabled) {
			setSubmitIsDisabled(true);
		}
	}, [formValues, hasErrors]);

	const StyledError = styled('div')(({ theme }) => ({
		display: 'flex',
		width: '100%',
		color: theme.palette.error.main,
		justifyContent: 'center',
		alignContent: 'center'
	}));

	const validateFormSubmit = () => {
		const errorsPresent = Object.values(hasErrors).length;
		if (errorsPresent || formValues === null) {
			return false;
		}

		for (const [key, value] of Object.entries(formValues)) {
			if (
				requiredFormValues.includes(key) &&
				(value === null || value === '')
			) {
				return false;
			}
		}

		let allEntriesEmpty = true;
		for (const value of Object.values(formValues)) {
			if (value !== null && value !== '') {
				allEntriesEmpty = false;
			}
		}

		if (allEntriesEmpty) {
			return false;
		}

		return true;
	};

	const handleFormValidate = (value, id, schema) => {
		const validate = {};
		validate[id] = value;

		if (schema && !options.disableValidation) {
			const inputIsValid = schema.validate(validate);

			if (!inputIsValid.error) {
				const errorClone = Object.assign({}, hasErrors);
				delete errorClone[id];
				setHasErrors(errorClone);
			} else {
				const newError = {};
				newError[id] = inputIsValid.error.message;

				console.log('Is there error?', inputIsValid.error.message);
				const errorClone = Object.assign({}, hasErrors, newError);
				setHasErrors(errorClone);
			}
		}

		const newInputValue = {};
		newInputValue[id] = value;
		const cloneFormValues = Object.assign({}, formValues, newInputValue);

		setFormValues(cloneFormValues);
	};

	const renderChildren = () => {
		const requiredInputs = [];
		const newFormInput = [];

		const render = (childElements) =>
			React.Children.map(childElements, (child) => {
				if (typeof child !== 'object') {
					return child;
				}

				if (child.props.children) {
					const nestedChildren = render(child.props.children);
					return cloneElement(child, null, nestedChildren);
				}

				if (child.props && child.props.id) {
					const childLabel = child.props.id;
					newFormInput[childLabel] = null;
				}

				if (child.props && child.props.required) {
					const childLabel = child.props.id;
					requiredInputs.push(childLabel);
				}

				const cloned = cloneElement(child, {
					errors: hasErrors,
					onInputChange: handleFormValidate,
					onInputFocus: (e) => null,
					disabled
				});

				return cloned;
			});
		const newChildren = render(children);

		const cloneFormValues = Object.assign({}, formValues, newFormInput);
		if (formValues === null) {
			setFormValues(cloneFormValues);
		}

		setRequiredFormValues(requiredInputs);
		setRenderedChildren(newChildren);
	};

	const _renderSubmit = () => {
		if (SubmitButton) {
			return <SubmitButton />;
		}

		if (options.disableSubmitButton) {
			return (
				<div style={{ display: 'none' }}>
					<DefaultSubmitButton
						disabled={
							!!Object.entries(hasErrors).length || submitIsDisabled || disabled
						}
						buttonText={options.submitButtonText}
					/>
				</div>
			);
		}

		if (options.disableValidation) {
			return (
				<DefaultSubmitButton
					disabled={disabled}
					buttonText={options.submitButtonText}
				/>
			);
		} else {
			return (
				<DefaultSubmitButton
					disabled={
						!!Object.entries(hasErrors).length || submitIsDisabled || disabled
					}
					buttonText={options.submitButtonText}
				/>
			);
		}
	};

	const _renderError = () => (
		<StyledError>
			<Typography variant="caption">{error}</Typography>
		</StyledError>
	);

	const handleSubmit = (event) => {
		event.preventDefault();

		for (const [key, value] of Object.entries(formValues)) {
			if (!value || value === null) {
				delete formValues[key];
			}
		}

		if (options.disableValidation) {
			onSubmit(formValues);
		} else {
			if (!Object.entries(hasErrors).length) {
				onSubmit(formValues);
			}
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<FormControl
				key="form-editor"
				style={{ display: 'flex', flexDirection: options.flexDirection }}
			>
				<Stack gap={gap}>
					{error && _renderError()}
					{renderedChildren}
					{_renderSubmit()}
				</Stack>
			</FormControl>
		</form>
	);
};

FormController.propTypes = {
	children: PropTypes.any,
	disabled: PropTypes.bool,
	error: PropTypes.string,
	gap: PropTypes.number,
	onSubmit: PropTypes.func,
	options: PropTypes.shape({
		disableSubmitButton: PropTypes.bool,
		disableValidation: PropTypes.bool,
		flexDirection: PropTypes.string,
		submitButtonText: PropTypes.string
	}),
	SubmitButton: PropTypes.func,
	useHook: PropTypes.func
};

export default FormController;
